/**
 * Amazon URL 判定 / ASIN 抽出ユーティリティ。
 *
 * 貼り付け検知 (paste ハンドラ) と、既存カードからの再取得の両方で使う。
 */

/** 短縮 URL のホスト */
const SHORT_HOSTS = new Set(['amzn.to', 'www.amzn.to', 'amzn.asia', 'a.co'])

/** /dp/ASIN, /gp/product/ASIN, /gp/aw/d/ASIN 等から 10 桁の ASIN を抜き出す */
const ASIN_PATH_PATTERN = /\/(?:dp|gp\/product|gp\/aw\/d|product)\/([A-Z0-9]{10})/i

/**
 * URL が Amazon (通常 or 短縮) 由来かを判定する。
 *
 * URL の parse に失敗した場合は `false` を返す。
 */
export const isAmazonUrl = (raw: string): boolean => {
  let parsed: URL
  try {
    parsed = new URL(raw.trim())
  } catch {
    return false
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false

  const host = parsed.host.toLowerCase()
  if (SHORT_HOSTS.has(host)) return true
  if (host === 'amazon.co.jp' || host.endsWith('.amazon.co.jp')) return true
  if (host === 'amazon.com' || host.endsWith('.amazon.com')) return true
  return false
}

/**
 * URL パスから ASIN (10 桁英数字) を取り出す。見つからなければ `null`。
 *
 * 短縮 URL はリダイレクト先が判明しないためこの段階では `null` になる。
 * その場合はサーバ側 (`/memos/amazon-product`) が最終 URL から ASIN を復元する。
 */
export const extractAsin = (raw: string): string | null => {
  try {
    const parsed = new URL(raw.trim())
    const match = ASIN_PATH_PATTERN.exec(parsed.pathname)
    return match ? match[1].toUpperCase() : null
  } catch {
    return null
  }
}

/**
 * 貼り付けテキストの先頭・末尾トリム後の全体が Amazon URL 一件かを判定する。
 *
 * 「文章の中に URL が混ざっている」ケースは検知対象外にし、
 * `www.amazon.co.jp/dp/XXXX` を丸ごと貼った時のみ変換 UI を出す想定。
 */
export const isSingleAmazonUrlPaste = (raw: string): string | null => {
  const trimmed = raw.trim()
  if (!trimmed) return null
  // 途中に空白・改行が混ざっていたら URL 単体貼りではない
  if (/\s/.test(trimmed)) return null
  return isAmazonUrl(trimmed) ? trimmed : null
}
